/**
 * Derived presentation facts about proof evidence: which check types a set of
 * items covers, how they group under a criterion and in what order they show.
 *
 * @module features/proofs/evidenceKinds
 */
import type { CriterionState } from '@chatsdet/types';
import { normalizeRunStatus } from '@chatsdet/ui';
import type { ProofCriterion, ProofEvidence } from './types';

/** `ui-test` is derived (an E2E item with checkpoints), never stored. */
type CheckType = 'e2e' | 'ui-test' | 'perf' | 'eval' | 'manual';

const CHECK_TYPE_ORDER: CheckType[] = ['e2e', 'ui-test', 'perf', 'eval', 'manual'];

export const CHECK_TYPE_GROUP_LABEL: Record<CheckType, string> = {
  e2e: 'Browser test',
  'ui-test': 'Screen check',
  perf: 'Speed check',
  eval: 'AI quality check',
  manual: 'Checked another way',
};

const checkTypeOf = (item: ProofEvidence): CheckType =>
  item.kind === 'perf' ? 'perf' : item.kind === 'eval' ? 'eval' : item.kind === 'manual' ? 'manual' : item.ui ? 'ui-test' : 'e2e';

const changedCount = (item: ProofEvidence) =>
  item.kind === 'e2e' && item.ui ? item.ui.checkpoints.filter((c) => c.status === 'changed').length : 0;

/** The distinct check types present, in display order, each with its chip label. */
export function checkTypeChips(items: ProofEvidence[]): { type: CheckType; label: string }[] {
  const present = new Set(items.map(checkTypeOf));
  const changed = items.reduce((sum, item) => sum + changedCount(item), 0);
  const labels: Record<CheckType, string> = {
    ...CHECK_TYPE_GROUP_LABEL,
    'ui-test': changed > 0 ? `${CHECK_TYPE_GROUP_LABEL['ui-test']} · ${changed} changed` : CHECK_TYPE_GROUP_LABEL['ui-test'],
  };
  return CHECK_TYPE_ORDER.filter((type) => present.has(type)).map((type) => ({ type, label: labels[type] }));
}

const startedAt = (item: ProofEvidence) =>
  item.kind === 'e2e' && item.run?.startedAt ? Date.parse(item.run.startedAt) : 0;

const rank = (item: ProofEvidence) => (item.status.kind === 'failed' ? 0 : item.status.kind === 'stale' ? 1 : 2);

/** Failed first, then stale, then newest first (items without a start time keep API order). */
export const sortEvidence = (items: ProofEvidence[]): ProofEvidence[] =>
  items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => rank(a.item) - rank(b.item) || startedAt(b.item) - startedAt(a.item) || a.index - b.index)
    .map(({ item }) => item);

/** Items of each present check type, in display order. */
export function groupEvidence(items: ProofEvidence[]): { type: CheckType; items: ProofEvidence[] }[] {
  return CHECK_TYPE_ORDER.map((type) => ({ type, items: sortEvidence(items.filter((i) => checkTypeOf(i) === type)) })).filter(
    (group) => group.items.length > 0
  );
}

/** A group opens when it is small or holds something that needs attention. */
export const groupStartsExpanded = (items: ProofEvidence[]): boolean =>
  items.length <= 3 || items.some((i) => i.status.kind !== 'proved');

/** True while a UI test's base run or comparisons are still being produced. */
export const hasPendingBase = (evidence: ProofEvidence[] | undefined): boolean =>
  (evidence ?? []).some((item) => {
    if (item.kind !== 'e2e' || !item.ui) return false;
    const baseRunning = item.ui.baseRun !== null && ['queued', 'running'].includes(normalizeRunStatus(item.ui.baseRun.status));
    return baseRunning || item.ui.checkpoints.some((c) => c.status === 'pending');
  });

export const sha7 = (sha: string) => sha.slice(0, 7);

export const evidenceKey = (item: ProofEvidence) =>
  item.kind === 'manual' ? `manual:${item.position}` : `${item.kind}:${item.runId}`;

/** Stale E2E rows only: the Re-run job skips every other kind. */
export const isStaleE2e = (item: ProofEvidence, headSha: string) => item.kind === 'e2e' && item.provedSha !== headSha;

/** One plain sentence on why a criterion is not fully proved; null when nothing needs explaining. */
export const criterionReason = (criterion: ProofCriterion): string | null => {
  const { status } = criterion;
  if (status.kind === 'failed') return status.problem;
  if (status.kind === 'not-proved') return status.reason;
  if (status.kind === 'stale') {
    return `This was last proved at an earlier version of the code (${sha7(status.provedSha)}) and has not been re-checked.`;
  }
  return null;
};

/** "3 proved by Cofactor · 1 checked another way · 1 not proved", from the server's states; zero counts left out. */
export const outcomeSummary = (criteria: ProofCriterion[]): string => {
  const count = (state: CriterionState) => criteria.filter((criterion) => criterion.state === state).length;
  return [
    [count('proved'), 'proved by Cofactor'],
    [count('checked-another-way'), 'checked another way'],
    [count('not-proved'), 'not proved'],
    [count('failed'), 'failed'],
  ]
    .filter(([n]) => n !== 0)
    .map(([n, label]) => `${n} ${label}`)
    .join(' · ');
};
